import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import DOMPurify from 'dompurify';
import { Box, Button, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { ICandidateDetails } from '@types';
import { PersonSignature } from 'components/common/PersonSignature/PersonSignature';
import { buttonSizeSx, ghostButtonSx } from 'theme/buttons';

const COLLAPSED_LINES = 6;

export const InterviewerFeedback: React.FC<{ data: ICandidateDetails }> = ({
  data,
}) => {
  const feedback = data.feedback?.feedback;
  const reviewer = data.feedback?.reviewer ?? null;
  const contentId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const wasExpanded = useRef(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return undefined;

    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
      setIsClamped(element.scrollHeight > lineHeight * (COLLAPSED_LINES + 1));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [feedback]);

  useLayoutEffect(() => {
    if (wasExpanded.current && !isExpanded) {
      toggleRef.current?.scrollIntoView({ block: 'nearest' });
    }
    wasExpanded.current = isExpanded;
  }, [isExpanded]);

  if (!feedback) return null;

  const isCollapsed = isClamped && !isExpanded;

  return (
    <Box>
      <Box position="relative">
        <Typography
          ref={contentRef}
          id={contentId}
          variant="body1"
          component="div"
          whiteSpace="pre-line"
          sx={(theme) => ({
            overflow: 'hidden',
            maxHeight: isCollapsed
              ? `calc(${COLLAPSED_LINES} * ${theme.typography.body1.lineHeight}em)`
              : 'none',
            '& > :first-child': { mt: 0 },
            '& > :last-child': { mb: 0 },
          })}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(feedback),
          }}
        />
        {isCollapsed && (
          <Box
            sx={(theme) => ({
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '3rem',
              pointerEvents: 'none',
              background: theme.palette.background.whiteLinearGradient,
            })}
          />
        )}
      </Box>
      {isClamped && (
        <Button
          ref={toggleRef}
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          endIcon={
            isExpanded ? (
              <ExpandLessIcon sx={{ width: 16, height: 16 }} />
            ) : (
              <ExpandMoreIcon sx={{ width: 16, height: 16 }} />
            )
          }
          sx={{ ...buttonSizeSx.md, ...ghostButtonSx, mt: 1.5, ml: -1.5 }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
      {Boolean(reviewer) && (
        <Box mt={1}>
          <PersonSignature person={reviewer} />
        </Box>
      )}
    </Box>
  );
};
